import { cn } from '@/lib/utils';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { Check, ChevronsUpDown, PlusCircle, X } from 'lucide-react';
import * as React from 'react';
import { Icon } from '../Icon';
import { Icons } from '../icons';
import { TrivialTooltip } from '../TrivialTooltip';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { FieldErrors } from './FieldErrors';
import Hint from './Hint';

/**
 * Combobox component with support for single or multiple selection.
 *
 * Features:
 * - Single or multi-select based on `limit` prop
 * - Search/filter functionality
 * - Inline option adding with `addInline` prop
 * - Custom option groups
 * - Clearable selections
 *
 * @example Basic usage with addInline:
 * ```tsx
 * <Combobox
 *   name="provider"
 *   options={providerOptions}
 *   value={selectedProvider}
 *   onChange={setSelectedProvider}
 *   addInline={true}
 *   limit={1}
 *   label="Select Provider"
 * />
 * ```
 *
 * When `addInline={true}`, users can type a new option that doesn't exist in the options list,
 * and it will be automatically added and selected. The selected value will have the format:
 * `{ $add: "user entered text" }`
 */

export type ComboboxPrimitive = string | number | boolean;

export interface ComboboxOption {
  value: ComboboxPrimitive;
  text: string | React.ReactNode;
  group?: string;
  note?: string;
  data?: any;
  className?: string;
}

// Single-select overload (limit = 1)
interface SingleComboboxProps<V = ComboboxPrimitive> {
  name: string;
  options: ComboboxOption[];
  value?: V;
  onChange: (value: V) => void;
  limit: 1;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  popoverClassName?: string;
  disabled?: boolean;
  showLabel?: boolean;
  label?: string;
  errors?: FormError;
  hint?: string | React.ReactNode;
  hintOnClick?: () => void;
  add?: (event?: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  addInline?: boolean;
  tooltip?: string;
  loading?: boolean;
  clearable?: boolean;
  afterClose?: (value: V) => void;
  button?: React.ReactNode;
  noDarkMode?: boolean;
  onClear?: () => void;
  alwaysReturnArray?: boolean;
  allowSelectAll?: boolean;
  popoverSide?: 'top' | 'bottom' | 'left' | 'right';
  truncateBadge?: boolean;
}

// Multi-select overload (limit > 1 or undefined)
interface MultiComboboxProps<V = ComboboxPrimitive> {
  name: string;
  options: ComboboxOption[];
  value?: V[];
  onChange: (value: V[]) => void;
  limit?: number;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  popoverClassName?: string;
  disabled?: boolean;
  showLabel?: boolean;
  label?: string;
  errors?: FormError;
  hint?: React.ReactNode;
  hintOnClick?: () => void;
  add?: (event?: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  addInline?: boolean;
  tooltip?: string;
  loading?: boolean;
  clearable?: boolean;
  afterClose?: (value: V[]) => void;
  button?: React.ReactNode;
  noDarkMode?: boolean;
  onClear?: () => void;
  alwaysReturnArray?: boolean;
  allowSelectAll?: boolean;
  popoverSide?: 'top' | 'bottom' | 'left' | 'right';
  truncateBadge?: boolean;
}

// Dynamic overload (for union types)
interface DynamicComboboxProps<V = ComboboxPrimitive> {
  name: string;
  options: ComboboxOption[];
  value?: V;
  onChange: (value: any) => void;
  limit?: number;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  popoverClassName?: string;
  disabled?: boolean;
  showLabel?: boolean;
  label?: string;
  errors?: FormError;
  hint?: string | React.ReactNode;
  hintOnClick?: () => void;
  add?: (event?: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  addInline?: boolean;
  tooltip?: string;
  loading?: boolean;
  clearable?: boolean;
  afterClose?: (value: any) => void;
  button?: React.ReactNode;
  noDarkMode?: boolean;
  onClear?: () => void;
  alwaysReturnArray?: boolean;
  allowSelectAll?: boolean;
  popoverSide?: 'top' | 'bottom' | 'left' | 'right';
  truncateBadge?: boolean;
}

// Union type for the component props
type ComboboxProps<V = ComboboxPrimitive> =
  | SingleComboboxProps<V>
  | MultiComboboxProps<V>
  | DynamicComboboxProps<V>;

// Component overloads
function Combobox<V = ComboboxPrimitive>(props: SingleComboboxProps<V>): React.ReactElement;
function Combobox<V = ComboboxPrimitive>(props: MultiComboboxProps<V>): React.ReactElement;
function Combobox<V = ComboboxPrimitive>(props: DynamicComboboxProps<V>): React.ReactElement;
function Combobox<V = ComboboxPrimitive>({
  name,
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No options found',
  className,
  labelClassName,
  inputClassName,
  popoverClassName,
  disabled = false,
  showLabel = true,
  label,
  errors,
  limit,
  hint,
  hintOnClick,
  add,
  addInline = false,
  tooltip,
  loading = false,
  clearable = false,
  afterClose,
  button,
  noDarkMode = false,
  onClear,
  alwaysReturnArray = false,
  allowSelectAll = false,
  popoverSide,
  truncateBadge = false,
}: ComboboxProps<V>) {
  const controlId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const listRef = React.useRef<HTMLDivElement>(null);
  const multiple = !limit || limit > 1;
  const EMPTY_VALUE_PLACEHOLDER = 'EMPTY_VALUE_PLACEHOLDER_EMPTY_VALUE_PLACEHOLDER';

  // Scroll list to top when search value changes to ensure filtered items are visible
  React.useEffect(() => {
    if (searchValue && listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [searchValue]);

  // Custom filter that prioritizes prefix and contains matches over fuzzy matching
  const customFilter = React.useCallback((itemValue: string, search: string) => {
    const normalizedValue = itemValue.toLowerCase();
    const normalizedSearch = search.toLowerCase();

    // Exact match
    if (normalizedValue === normalizedSearch) return 1;

    // Starts with search term (high priority)
    if (normalizedValue.startsWith(normalizedSearch)) return 0.9;

    // Contains search term (medium-high priority)
    if (normalizedValue.includes(normalizedSearch)) return 0.8;

    // Word boundary match (e.g., "STM-Masseter" matches "Mas")
    const words = normalizedValue.split(/[-_\s]+/);

    for (const word of words) {
      if (word.startsWith(normalizedSearch)) return 0.7;
    }

    // Collapsed delimiter match: normalize both strings by removing delimiters
    // This allows "stm-m" to match "STM- Masseter" by comparing "stmm" to "stm masseter" → "stmmasseter"
    const collapsedValue = normalizedValue.replace(/[-_\s]+/g, '');
    const collapsedSearch = normalizedSearch.replace(/[-_\s]+/g, '');

    if (collapsedValue.startsWith(collapsedSearch)) return 0.6;
    if (collapsedValue.includes(collapsedSearch)) return 0.5;

    // No match
    return 0;
  }, []);

  const stringify = (val: ComboboxPrimitive) =>
    val === '' ? EMPTY_VALUE_PLACEHOLDER : String(val);

  // Helper function to extract searchable text from option.text
  const getSearchableText = (text: string | React.ReactNode): string => {
    if (typeof text === 'string') {
      return text;
    }

    // For React nodes, we'll fallback to the stringified value
    // This could be enhanced to extract text from React elements if needed
    return '';
  };

  // Ensure all options have a valid, non-empty value and searchable text
  const validOptions =
    options?.map(option => ({
      ...option,
      _stringVal: stringify(option.value),
      _searchText: getSearchableText(option.text) || stringify(option.value),
    })) ?? [];

  // Handle inline adding of new options
  const handleAddInline = (searchText: string) => {
    if (!addInline || !searchText.trim()) return;

    // Add the new option and select it
    if (multiple) {
      const newValues = [...selectedValues, searchText as ComboboxPrimitive];
      const converted = newValues.map(v => (v === EMPTY_VALUE_PLACEHOLDER ? '' : v));
      (onChange as (value: ComboboxPrimitive[]) => void)(converted as ComboboxPrimitive[]);
    } else {
      if (alwaysReturnArray) {
        (onChange as (value: ComboboxPrimitive[]) => void)([
          { $add: searchText } as unknown as ComboboxPrimitive,
        ]);
      } else {
        (onChange as (value: ComboboxPrimitive) => void)({
          $add: searchText,
        } as unknown as ComboboxPrimitive);
      }
    }

    setOpen(false);
  };

  // Convert values to array for consistent handling
  const rawSelectedValues =
    multiple || alwaysReturnArray ? (Array.isArray(value) ? value : value ? [value] : []) : [value];

  // Handle values with $add property for display
  const getDisplayValue = (val: any) => {
    if (val && typeof val === 'object' && val.$add) {
      return val.$add;
    }

    return val === '' ? EMPTY_VALUE_PLACEHOLDER : val;
  };

  // Normalize selected values to match placeholder replacement and handle $add values
  const selectedValues = rawSelectedValues.map(v => getDisplayValue(v));
  const selectedOptions = selectedValues
    .map(val => {
      // First try to find in existing options (account for empty-string placeholder)
      const existingOption = validOptions.find(
        option => option.value === val || option._stringVal === stringify(val as any)
      );

      if (existingOption) {
        return existingOption;
      }

      // If not found and it's not the placeholder, create a display option for $add values
      if (val !== EMPTY_VALUE_PLACEHOLDER) {
        const originalValue = rawSelectedValues.find(
          rv => rv && typeof rv === 'object' && (rv as any).$add === val
        );

        if (originalValue) {
          return {
            value: originalValue,
            text: val,
            _stringVal: stringify(val),
            _searchText: val,
          };
        }
      }

      return null;
    })
    .filter((option): option is NonNullable<typeof option> => option !== null);

  const handleSelect = (optionVal: ComboboxPrimitive) => {
    const outputValue = optionVal === EMPTY_VALUE_PLACEHOLDER ? '' : optionVal;

    if (multiple) {
      const newValues = selectedValues.includes(outputValue as V)
        ? selectedValues.filter(v => v !== (outputValue as V))
        : limit && selectedValues.length >= limit
          ? selectedValues
          : [...selectedValues, outputValue as ComboboxPrimitive];

      const converted = newValues.map(v => (v === EMPTY_VALUE_PLACEHOLDER ? '' : v));
      (onChange as (value: ComboboxPrimitive[]) => void)(converted as ComboboxPrimitive[]);
    } else {
      const finalValue = outputValue === selectedValues[0] ? '' : outputValue;

      if (alwaysReturnArray && !Array.isArray(finalValue)) {
        (onChange as (value: ComboboxPrimitive[]) => void)([finalValue as ComboboxPrimitive]);
      } else {
        (onChange as (value: ComboboxPrimitive) => void)(finalValue as ComboboxPrimitive);
      }

      setOpen(false);
    }
  };

  const removeValue = (optionValue: ComboboxPrimitive) => {
    if (multiple) {
      // Filter out from the original rawSelectedValues, not the display values
      const newValues = rawSelectedValues.filter(rv => {
        if (rv && typeof rv === 'object' && (rv as any).$add) {
          return (rv as any).$add !== optionValue;
        }

        return rv !== optionValue;
      });
      (onChange as (value: ComboboxPrimitive[]) => void)(newValues as ComboboxPrimitive[]);
    }
  };

  const hasValue = multiple
    ? Array.isArray(value)
      ? value.length > 0
      : false
    : value !== undefined && value !== null && value !== '';

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (multiple) {
      (onChange as (value: ComboboxPrimitive[]) => void)([]);
    } else {
      (onChange as (value: ComboboxPrimitive) => void)('' as ComboboxPrimitive);
    }

    if (onClear) {
      onClear();
    }
  };

  const onOpenChange = (openState: boolean) => {
    setOpen(openState);

    if (!openState) {
      afterClose?.(value);
    }
  };

  // Select all functionality
  const allOptionsSelected =
    multiple &&
    allowSelectAll &&
    validOptions.length > 0 &&
    validOptions.every(option => selectedValues.includes(option.value as V));

  const canSelectAll =
    multiple &&
    allowSelectAll &&
    validOptions.length > 0 &&
    (!limit || validOptions.length <= limit);

  const handleSelectAll = () => {
    if (multiple) {
      if (allOptionsSelected) {
        // Deselect all
        (onChange as (value: ComboboxPrimitive[]) => void)([]);
      } else {
        // Select all (respecting limit if set)
        const allValues = validOptions.map(option =>
          option.value === EMPTY_VALUE_PLACEHOLDER ? '' : option.value
        );
        const valuesToSelect = limit ? allValues.slice(0, limit) : allValues;
        (onChange as (value: ComboboxPrimitive[]) => void)(valuesToSelect as ComboboxPrimitive[]);
      }
    }
  };

  return (
    <div className={cn('flex flex-col gap-1 min-w-0', className)}>
      {showLabel && label && (
        <Label
          htmlFor={button ? name : controlId}
          className={cn('text-sm font-medium', labelClassName)}
        >
          {label}
          {!!tooltip && <TrivialTooltip text={tooltip} type="info" />}
        </Label>
      )}
      <div className={cn('flex flex-col w-full', inputClassName)}>
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger
            asChild
            className={cn(
              'border-gray-200',
              noDarkMode
                ? 'dark:bg-white dark:border-gray-200'
                : 'dark:bg-dark-gray-700 dark:border-gray-700'
            )}
          >
            {button ? (
              button
            ) : (
              <Button
                id={controlId}
                variant="outline"
                role="combobox"
                aria-label={label}
                aria-expanded={open}
                aria-busy={loading || undefined}
                className={cn(
                  'w-full justify-between min-h-8 bg-white hover:bg-white overflow-hidden',
                  multiple && selectedValues.length > 0 ? 'h-auto' : '',
                  className
                )}
                disabled={disabled}
              >
                {multiple ? (
                  <div
                    className={cn(
                      'flex flex-wrap gap-1 items-center',
                      !!truncateBadge && 'flex-1 min-w-0'
                    )}
                  >
                    {selectedValues.length > 0 ? (
                      selectedOptions.map(option => (
                        <Badge
                          key={option._stringVal}
                          variant="secondary"
                          className={cn(
                            !!truncateBadge && 'shrink min-w-0 w-fit max-w-full justify-start'
                          )}
                        >
                          {truncateBadge ? (
                            <span className="truncate min-w-0">{option.text}</span>
                          ) : (
                            option.text
                          )}
                          {!disabled && (
                            <div
                              className="ml-1 shrink-0 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Handle both regular values and $add values
                                const valueToRemove =
                                  option.value &&
                                  typeof option.value === 'object' &&
                                  (option.value as any).$add
                                    ? (option.value as any).$add
                                    : option.value;
                                removeValue(valueToRemove as ComboboxPrimitive);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </div>
                          )}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground font-normal">{placeholder}</span>
                    )}
                  </div>
                ) : (
                  <span className={cn(!selectedOptions?.length && 'text-muted-foreground')}>
                    {selectedOptions.length > 0 ? selectedOptions[0].text : placeholder}
                  </span>
                )}
                {loading ? (
                  <Icon
                    icon={Icons.loading}
                    className="opacity-50 ml-2 h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none"
                  />
                ) : (
                  <div className="flex items-center gap-1">
                    {clearable && hasValue && !disabled && (
                      <div
                        className="opacity-50 hover:opacity-100 cursor-pointer"
                        onClick={handleClear}
                      >
                        <Icon icon={Icons.close} className="h-4 w-4 shrink-0 opacity-50" />
                      </div>
                    )}
                    <ChevronsUpDown className="opacity-50 h-4 w-4 shrink-0" />
                  </div>
                )}
              </Button>
            )}
          </PopoverTrigger>
          <PopoverContent
            className={cn('w-[var(--radix-popover-trigger-width)] p-0', popoverClassName)}
            side={popoverSide}
            onWheel={e => e.stopPropagation()}
          >
            <Command
              className={cn(noDarkMode ? 'bg-white' : 'bg-white dark:bg-dark-gray-700')}
              filter={customFilter}
            >
              <div className="relative">
                <CommandInput
                  // aria-label={label ? `Search ${label.toLowerCase()}` : 'Search options'}
                  placeholder={searchPlaceholder}
                  className="h-9"
                  value={searchValue}
                  onValueChange={setSearchValue}
                />
                {add && !disabled && (
                  <PlusCircle
                    className={cn(
                      'h-4 w-4 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 cursor-pointer absolute right-2 top-2.5',
                      noDarkMode && 'dark:text-gray-400 dark:hover:text-gray-500'
                    )}
                    onClick={add}
                  />
                )}
              </div>
              <CommandList ref={listRef}>
                <CommandEmpty>
                  <div className="py-2 px-4 flex flex-col items-center text-center">
                    {emptyMessage}
                    {addInline && searchValue.trim() && !disabled && (
                      <Button
                        variant="outline"
                        className="mt-2"
                        onClick={() => handleAddInline(searchValue)}
                        size="sm"
                      >
                        <PlusCircle className="h-4 w-4" />
                        Add "{searchValue}"
                      </Button>
                    )}
                    {add && !disabled && !addInline && (
                      <Button variant="outline" className="mt-2" onClick={() => add()} size="sm">
                        <PlusCircle className="h-4 w-4" />
                        Add new {label?.toLowerCase() || 'item'}
                      </Button>
                    )}
                  </div>
                </CommandEmpty>
                {(() => {
                  // Group options by the `group` key (fallback to empty string)
                  const grouped: Record<string, typeof validOptions> = {};
                  validOptions.forEach(opt => {
                    const grp = opt.group ?? '';
                    if (!grouped[grp]) grouped[grp] = [];
                    grouped[grp].push(opt);
                  });

                  return (
                    <>
                      {/* Select all option */}
                      {canSelectAll && (
                        <CommandGroup>
                          <CommandItem
                            onSelect={handleSelectAll}
                            className="font-medium"
                            value="select-all-items"
                          >
                            {allOptionsSelected ? 'Deselect all' : 'Select all'}
                            <Check
                              className={cn(
                                'ml-auto h-4 w-4',
                                allOptionsSelected ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                          </CommandItem>
                        </CommandGroup>
                      )}

                      {/* Regular options */}
                      {Object.entries(grouped).map(([grp, items]) => (
                        <CommandGroup key={`group-${grp || 'default'}`} heading={grp || undefined}>
                          {items.map(option => (
                            <CommandItem
                              key={option._stringVal}
                              value={option._searchText}
                              onSelect={() => handleSelect(option.value)}
                              disabled={
                                multiple && limit
                                  ? selectedValues.length >= limit &&
                                    !selectedValues.includes(option.value as V)
                                  : false
                              }
                              className={option?.className}
                            >
                              <div className="flex w-full items-center gap-2">
                                <span>{option.text}</span>
                                {option.note ? (
                                  <span
                                    className="ml-auto text-xs text-gray-500 dark:text-dark-gray-300"
                                    title={option.note}
                                  >
                                    {option.note}
                                  </span>
                                ) : (
                                  <span className="ml-auto" />
                                )}
                                <Check
                                  className={cn(
                                    'h-4 w-4',
                                    selectedValues.includes(option.value as V)
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                              </div>
                            </CommandItem>
                          ))}

                          {/* add button within each group only for first group */}
                          {add &&
                            !disabled &&
                            items.length > 0 &&
                            grp === Object.keys(grouped)[0] &&
                            !addInline && (
                              <CommandItem
                                onSelect={() => add()}
                                className="text-primary"
                                value="add-new-item"
                              >
                                <PlusCircle className="h-4 w-4" />
                                Add new {label?.toLowerCase() || 'item'}
                              </CommandItem>
                            )}
                        </CommandGroup>
                      ))}
                    </>
                  );
                })()}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {!!hint && <Hint onClick={hintOnClick}>{hint}</Hint>}
        <FieldErrors errors={errors} />
      </div>
    </div>
  );
}

export default Combobox;
